// Copyright 2025, University of Maryland, All Rights Reserved
import { useDatasourceBills } from '@/hooks/datasource-bills/useDatasourceBills'
import { useDatasourceState } from '@/hooks/admin/useDatasourceState'
import { DataSourceSelector } from '@/components/datasource-bills/DataSourceSelector'
import { SyncSettingsCard } from '@/components/datasource-bills/SyncSettingsCard'
import { BillsTableCard } from '@/components/datasource-bills/BillsTableCard'
import type { APISession } from '@/types/session'
import { getLatestAPISession } from '@/types/session'

export default function DatasourceBillsPage() {
  // State management hooks
  const {
    selectedDataSource,
    setSelectedDataSource,
    isSyncSettingsExpanded,
    setIsSyncSettingsExpanded,
    expandedSessions,
    toggleSessionExpanded,
    API_SESSIONS,
    DATA_SOURCES
  } = useDatasourceState()

  // Bills data hook
  const {
    bills,
    isLoading,
    selectedSession: billsSelectedSession,
    setSelectedDataSource: setBillsDataSource,
    setSelectedSession: setBillsSelectedSession,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    totalFilteredItems,
    refetch,
    loadAllBills,
    isLoadingAll,
    allBillsLoaded,
    allBillsCount
  } = useDatasourceBills('Maryland', getLatestAPISession())

  // Event handlers
  const handleDataSourceChange = (dataSource: typeof selectedDataSource) => {
    setSelectedDataSource(dataSource)
    setBillsDataSource(dataSource)
  }

  const handleSessionChange = (value: string) => {
    if (API_SESSIONS.includes(value as APISession)) {
      setBillsSelectedSession(value as APISession)
    }
  }

  const handleRefresh = () => {
    refetch()
  }

  const handleLoadAll = () => {
    loadAllBills()
  }

  return (
    <div className="container mx-auto p-2 sm:p-4 md:p-6 space-y-6">
      {/* Data Source Selection */}
      <DataSourceSelector
        selectedDataSource={selectedDataSource}
        onDataSourceChange={handleDataSourceChange}
        dataSources={DATA_SOURCES}
      />

      {/* Sync Settings Card */}
      <SyncSettingsCard
        selectedDataSource={selectedDataSource}
        apiSessions={API_SESSIONS}
        isExpanded={isSyncSettingsExpanded}
        onToggleExpanded={() => setIsSyncSettingsExpanded(!isSyncSettingsExpanded)}
        expandedSessions={expandedSessions}
        onToggleSessionExpanded={toggleSessionExpanded}
      />

      {/* Datasource Bills Card */}
      <BillsTableCard
        selectedDataSource={selectedDataSource}
        apiSessions={API_SESSIONS}
        billsSelectedSession={billsSelectedSession}
        onSessionChange={handleSessionChange}
        onRefresh={handleRefresh}
        onLoadAll={handleLoadAll}
        bills={bills}
        isLoading={isLoading}
        isLoadingAll={isLoadingAll}
        allBillsLoaded={allBillsLoaded}
        allBillsCount={allBillsCount}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        totalFilteredItems={totalFilteredItems}
      />
    </div>
  )
}
